import { getLedgerCommercialTimingSignals } from '../../ledger-cache.js';
import {
  CASH_EVENT_TYPES,
  CASH_LABELS,
  dateInRange,
  dateKey,
} from './cash-monitor-model.js';

const POSTING_POSTURE = 'PRE_POSTING_COMMERCIAL_TIMING';
const INACTIVE_STATUSES = new Set(['CONVERTED', 'PARTIALLY_CONVERTED', 'CANCELLED', 'EXPIRED']);
const SALES_ORDER_TIMING_STATUSES = new Set(['SENT', 'ACCEPTED']);
const PURCHASE_ORDER_TIMING_STATUSES = new Set(['SENT', 'ACKNOWLEDGED']);

function cleanAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function timingAmount(signal) {
  return cleanAmount(signal?.amountInDefault ?? signal?.amount);
}

function safeDateKey(value) {
  try {
    return dateKey(value);
  } catch {
    return null;
  }
}

function isPrePostingSignal(signal) {
  return signal?.postingPosture === POSTING_POSTURE && signal?.accountingTruth === false;
}

function activeCommercialStatus(signal) {
  const status = String(signal?.status || '').toUpperCase();
  if (!status || INACTIVE_STATUSES.has(status)) return false;
  if (signal?.sourceEntityType === 'SalesOrder') return SALES_ORDER_TIMING_STATUSES.has(status);
  if (signal?.sourceEntityType === 'PurchaseOrder') return PURCHASE_ORDER_TIMING_STATUSES.has(status);
  return false;
}

function signalType(signal) {
  if (signal?.timingKind === 'EXPECTED_RECEIPT' && signal?.sourceEntityType === 'SalesOrder') {
    return CASH_EVENT_TYPES.EXPECTED_RECEIPT;
  }
  if (signal?.timingKind === 'COMMITTED_OBLIGATION' && signal?.sourceEntityType === 'PurchaseOrder') {
    return CASH_EVENT_TYPES.COMMITTED_OBLIGATION;
  }
  return null;
}

function labelsForType(type) {
  if (type === CASH_EVENT_TYPES.EXPECTED_RECEIPT) {
    return [CASH_LABELS.EXPECTED_RECEIPTS, CASH_LABELS.ASSUMPTION];
  }
  if (type === CASH_EVENT_TYPES.COMMITTED_OBLIGATION) {
    return [CASH_LABELS.COMMITTED_OBLIGATIONS, CASH_LABELS.ASSUMPTION];
  }
  return [];
}

function titleForSignal(signal) {
  const prefix = signal.sourceEntityType === 'SalesOrder' ? 'SO' : 'PO';
  const document = signal.documentNumber || signal.sourceEntityId;
  const contact = signal.contactName ? ` - ${signal.contactName}` : '';
  return `${prefix} ${document}${contact}`;
}

export function buildLedgerCommercialTimingEvents({ startDate, horizonDays, signals } = {}) {
  const safeStart = dateKey(startDate || new Date());
  const safeHorizon = Math.max(1, Number(horizonDays) || 45);
  const sourceSignals = Array.isArray(signals) ? signals : getLedgerCommercialTimingSignals();
  const events = [];

  for (const signal of sourceSignals || []) {
    const type = signalType(signal);
    const amount = timingAmount(signal);
    const eventDate = safeDateKey(signal?.date);
    if (!type || amount <= 0 || !isPrePostingSignal(signal) || !activeCommercialStatus(signal)) continue;
    if (!eventDate || !dateInRange(eventDate, safeStart, safeHorizon)) continue;

    events.push({
      id: signal.id || `ledger-commercial-timing:${signal.sourceEntityType}:${signal.sourceEntityId}`,
      date: eventDate,
      type,
      amount,
      title: titleForSignal(signal),
      account: null,
      appliesTo: ['pay-on-due-date', 'agreed-schedule', 'tight-budget'],
      labels: labelsForType(type),
      source: `${signal.source || 'cleic-ledger'}:${signal.sourceEntityType}`,
      sourceEntityType: signal.sourceEntityType,
      sourceEntityId: signal.sourceEntityId,
      documentNumber: signal.documentNumber,
      contactId: signal.contactId,
      contactName: signal.contactName,
      sourceStatus: signal.status,
      timingKind: signal.timingKind,
      postingPosture: signal.postingPosture,
      accountingTruth: signal.accountingTruth,
      provenanceKind: 'ledger-commercial-timing',
      sourceLabel: 'Ledger commercial timing',
      evidence: {
        source: signal.source || 'cleic-ledger',
        sourceEntityType: signal.sourceEntityType,
        sourceEntityId: signal.sourceEntityId,
        documentNumber: signal.documentNumber,
        status: signal.status,
        currencyCode: signal.currencyCode,
        amount: signal.amount,
        amountInDefault: signal.amountInDefault,
        postingPosture: signal.postingPosture,
        accountingTruth: signal.accountingTruth,
      },
    });
  }

  return events.sort((a, b) => (
    a.date.localeCompare(b.date) ||
    String(a.title || a.id).localeCompare(String(b.title || b.id))
  ));
}
